import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Back from "@/components/specific/Back";
import Button from "@/components/common/Button";
import Input from "@/components/common/inputs/Input";
import { useLoader } from "@/hooks/LoaderProvider";
import {
  answerSecurityQuestionAsync,
  addSecurityQuestionAsync,
} from "@/store/redux/slices/settings/settings_slice";

const AnswerSecurityQuestionModal = ({
  isVisible,
  onClose,
  question,
  questionId,
  existingAnswer = "",
  isEditMode = false,
  isNewQuestion = false,
}) => {
  const [answer, setAnswer] = useState(
    isEditMode && existingAnswer ? existingAnswer : ""
  );
  const [isActive, setIsActive] = useState(
    isEditMode && existingAnswer ? true : false
  );
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();

  useEffect(() => {
    if (isVisible) {
      if (isEditMode) {
        // In edit mode, use existing answer if available
        setAnswer(existingAnswer || "");
        setIsActive(!!existingAnswer);
      } else {
        // In new/add mode, always start empty
        setAnswer("");
        setIsActive(false);
      }
    } else {
      setAnswer("");
      setIsActive(false);
    }
  }, [isVisible, isEditMode, existingAnswer]);

  useEffect(() => {
    setIsActive(answer.trim().length > 0);
  }, [answer]);

  const handleProceed = async () => {
    if (!answer.trim()) return;

    onClose()

    try {
      showLoader();

      if (isNewQuestion) {
        // New flow: send questionId + answer in ONE request.
        await dispatch(
          addSecurityQuestionAsync({
            questionId,
            answer: answer.trim(),
          })
        ).unwrap();
      } else {
        // Existing recovery question (first-time answer or edit):
        // send the link _id + answer to the dedicated endpoint.
        await dispatch(
          answerSecurityQuestionAsync({
            _id: questionId,
            answer: answer.trim(),
          })
        ).unwrap();
      }

      hideLoader();
      // Close the modal immediately after successful save.
      // Success / error feedback is handled in the parent component
      // via Redux state (settings slice).
    } catch (error) {
      hideLoader();
      // Let the parent + global error handling surface any feedback.
      console.error("Failed to process security question:", error);
    }
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={null}
      centered
      width={410}
      closeIcon={null}
      styles={{
        body: { padding: 0 },
        content: {
          padding: 0,
          borderRadius: "16px",
          overflow: "hidden",
        },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
      }}>
      <div className="flex flex-col bg-white">
        {/* Header */}
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center">
            {!isEditMode && (
              <Back removeText={true} arrowColor="black" onClick={onClose} />
            )}
            <div className={`flex flex-col ${!isEditMode ? "ml-3" : ""}`}>
              <h2 className="text-bold-x text-primary">
                {isEditMode
                  ? "Edit Security Question"
                  : "Answer Security Question"}
              </h2>
              {isEditMode && (
                <p className="text-small text-lighterGrey">
                  Modify your existing security question
                </p>
              )}
            </div>
          </div>
          <CloseOutlined
            onClick={onClose}
            className="cursor-pointer"
            style={{
              color: "var(--veegil-color-primary)",
              backgroundColor: "var(--veegil-color-lighter-blue)",
              borderRadius: "50%",
              padding: "4px",
            }}
          />
        </div>

        {/* Body */}
        <div className="p-6">
          <h3 className="text-bold-x text-center font-semibold mb-4">
            {question}
          </h3>

          {isEditMode && existingAnswer && (
            <p className="text-small text-center mb-3">
              Currently set as{" "}
              <span className="font-bold">{existingAnswer}</span>
            </p>
          )}

          <Input
            label="ANSWER"
            placeholder="Enter your answer"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
          />
        </div>

        {/* Footer */}
        <div className="w-full bg-secondary p-4">
          <Button
            text="Save"
            onClick={handleProceed}
            isActive={isActive}
            color={isActive ? "primary" : "darkBlue"}
            className="w-full"
            customTextColor={isActive ? "white" : "primary"}
          />
        </div>
      </div>
    </Modal>
  );
};

AnswerSecurityQuestionModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  question: PropTypes.string,
  questionId: PropTypes.string,
  existingAnswer: PropTypes.string,
  isEditMode: PropTypes.bool,
  isNewQuestion: PropTypes.bool,
};

export default AnswerSecurityQuestionModal;
